import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";

//components
import { LoadingComponent } from "../../components/layout/loading/LoadingComponent";
import SwishCheckoutSignalR from "./SwishCheckoutSignalR";
import QrCodeDialog from "../../components/payment/swish/swishQrCodeDialogComponent";
import SwishErrorComponent from "../../components/payment/swish/swishErrorComponent";

// redux
import { setLoading, setSwishPaymentResponseError } from "../../redux/actions";

// API
import {
  startSwishPayment,
  getProtectedOrder,
} from "../../utils/api/restClient";

// Translation
import { translatePage } from "../../utils/services/translate";

// Images
import swishImg from "../../assets/icons/payment/Swish_Logo_Primary.svg";

// scss
import "./swishCheckoutPage.scss";
import SwishCountDownTimerComponent from "../../components/payment/swish/swishCountDownTimerComponent";

function SwishCheckoutPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const swishResponse = useSelector(
    (state) => state.Payment.swish_response_status
  );
  const loading = useSelector((state) => state.Layout.loading);
  const translations = useSelector((state) => state.Layout.translations);
  const language = useSelector((state) => state.Layout.language);

  const { id } = useParams();
  const [token, setToken] = useState(null);
  const [order, setOrder] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [orderNotFoundError, setOrderNotFoundError] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const handleOpenDialog = () => setShowDialog(true);
  const handleCloseDialog = () => setShowDialog(false);
  const pendingStatusContainer = showDialog
    ? "container min-height-100 d-flex justify-content-center align-items-center blur-background"
    : "container min-height-100 d-flex justify-content-center align-items-center ";

  const startSwishPaymentRequest = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const res = await startSwishPayment(id);
      if (res.swishIntegrationResponse.tokenResponse) {
        setToken(res.swishIntegrationResponse.tokenResponse.token);
        dispatch(
          setSwishPaymentResponseError(
            id,
            res.swishIntegrationResponse.status.status,
            res.swishIntegrationResponse.status.errorMessage,
            res.swishIntegrationResponse.status.errorCode
          )
        );
      } else {
        setApiError(true);
      }
    } catch (error) {
      setApiError(true);
    } finally {
      dispatch(setLoading(false));
    }
  }, [id, dispatch]);

  const getOrderFromApi = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      const res = await getProtectedOrder(id);
      return res;
    } catch (error) {
    } finally {
      dispatch(setLoading(false));
    }
  }, [id, dispatch]);

  function isOlderThanFourMinutes(order) {
    if (!order || !order.createdDateTime) {
      return false;
    }

    const createdTime = new Date(order.createdDateTime);
    const currentTime = new Date();
    const fourMinutes = 4 * 60 * 1000; // 4 minutes
    return currentTime - createdTime > fourMinutes;
  }
  const openSwishAppOnClick = () => {
    if (token) {
      const callbackUrl = `https://nobina-biljetten.se/payment/swish/${id}`;
      const swishUrl = `swish://paymentrequest?token=${token}&callbackurl=${callbackUrl}`;
      window.open(swishUrl, "_blank");
    }
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  });

  useEffect(() => {
    getOrderFromApi(id).then((res) => {
      if (res !== undefined && res.referenceId === id) {
        setOrder(res);
        startSwishPaymentRequest(id);
      } else {
        setOrderNotFoundError(true);
      }
    });
  }, [id]);

  if (loading) {
    return <LoadingComponent />;
  }

  if (
    order !== null &&
    swishResponse !== null &&
    order.referenceId !== swishResponse.orderReference
  ) {
    return <LoadingComponent />;
  }
  if (
    order !== null &&
    swishResponse !== null &&
    order.referenceId === swishResponse.orderReference &&
    order.status === "paid"
  ) {
    navigate("/t/" + order.referenceId);
  }
  if (orderNotFoundError) {
    return (
      <div className="container pt-5 min-vh-100">
        <div className="row">
          <div className="col-12 text-center">
            <h1 className="mt-5 text-center">
              {" "}
              {translatePage(
                translations,
                language,
                "swishCheckoutPage",
                "errorOrderNotFoundH1"
              )}
            </h1>
            <p className="text-m">id: {id}</p>
          </div>
        </div>
      </div>
    );
  }
  if (apiError) {
    return (
      <div className="container min-height-100  d-flex justify-content-center align-items-center">
        <div className="swishErrorContainer">
          <div className="row">
            <div className="col-12">
              <h1 className="mt-5 ps-2 pe-2">
                {translatePage(
                  translations,
                  language,
                  "swishCheckoutPage",
                  "errorTechnicalh1"
                )}
              </h1>
              <p className="ps-2 pe-2">
                {translatePage(
                  translations,
                  language,
                  "swishCheckoutPage",
                  "errorTechnicalMessage"
                )}
              </p>
            </div>
          </div>
          <div className="col-12 text-center pt-4">
            <button
              className="btn btn-secondary w-100"
              onClick={() => navigate("/paymentcheck")}
            >
              {translatePage(
                translations,
                language,
                "swishCheckoutPage",
                "changePaymentMethodButton"
              )}
            </button>
          </div>
          <div className="col-12 text-center mt-2">
            <button
              className="btn btn-success w-100"
              onClick={() => startSwishPaymentRequest(id)}
            >
              {translatePage(
                translations,
                language,
                "swishCheckoutPage",
                "tryAgainButton"
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (swishResponse === null) {
    return (
      <div className="container mt-5 pt-5 min-height-100 ">
        <div className="row mt-5">
          <div className="col-12"></div>
        </div>
      </div>
    );
  }
  if (swishResponse !== null && swishResponse.status === "PENDING") {
    if (isOlderThanFourMinutes(order)) {
      return (
        <>
          <div className={pendingStatusContainer}>
            <div className="swish-container">
              <div className="row">
                <div className="col-12 ">
                  <h1 className="text-danger text-center">
                    {translatePage(
                      translations,
                      language,
                      "swishCheckoutPage",
                      "errorOrderTimedOutH1"
                    )}
                  </h1>
                  <div className="border">
                    <p className="text-m p-3 mt-2 ms-2 pb-0">
                      {translatePage(
                        translations,
                        language,
                        "swishCheckoutPage",
                        "errorOrderTimedOutMessage1"
                      )}
                    </p>
                    <p className="text-m p-3 mt-2 ms-2 pb-0">
                      {translatePage(
                        translations,
                        language,
                        "swishCheckoutPage",
                        "errorOrderTimedOutMessage2"
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      );
    } else {
      const swishPaymentStartTime = new Date(order.createdDateTime);
      const swishPaymentEndTime = new Date(
        swishPaymentStartTime.getTime() + 4 * 60000
      );
      const serverTime = new Date().toISOString(); // TODO: should me serverTime from API.
      return (
        <>
          <div className={pendingStatusContainer}>
            <div className="swish-container">
              <div className="row mt-5">
                <div className="col-12 ">
                  <h1 className="text-center">
                    {translatePage(
                      translations,
                      language,
                      "swishCheckoutPage",
                      "swishWaitingForConfirmh1"
                    )}
                  </h1>
                  <p className="p-3">
                    {translatePage(
                      translations,
                      language,
                      "swishCheckoutPage",
                      "swishWaitingForConfirmMessage1"
                    )}
                  </p>
                  <div className="border">
                    <p className="text-m p-3 mt-2 ms-2 pb-0">
                      {translatePage(
                        translations,
                        language,
                        "swishCheckoutPage",
                        "swishWaitingForConfirmMessage2"
                      )}
                    </p>
                    <SwishCountDownTimerComponent
                      serverTime={serverTime}
                      endTime={swishPaymentEndTime.toISOString()}
                    />

                    <div className="ps-2 me-4 pb-3">
                      <div className="swish-icon-container">
                        <a
                          className="btn btn-success text-m text-center w-100 "
                          href={`swish://paymentrequest?token=${token}&callbackurl=https://nobina-biljetten.se/payment/swish/cb/${id}`}
                        >
                          <img
                            src={swishImg}
                            alt="swishImg"
                            className="swish-icon"
                          />
                          {translatePage(
                            translations,
                            language,
                            "swishCheckoutPage",
                            "openSwishButtonText"
                          )}
                        </a>
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn btn-link text-m text-center w-100"
                    onClick={handleOpenDialog}
                  >
                    {translatePage(
                      translations,
                      language,
                      "swishCheckoutPage",
                      "openSwishButtonOption2Text"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="">
            {showDialog && (
              <QrCodeDialog
                show={showDialog}
                handleClose={handleCloseDialog}
                token={token}
              />
            )}
          </div>
          {/* <SwishCheckoutSignalR orderReference={id} /> */}
        </>
      );
    }
  }

  if (swishResponse !== null && swishResponse.status !== "PENDING") {
    if (swishResponse.status === "PAID") {
      setTimeout(() => {
        if (order !== null) {
          navigate("/t/" + order.referenceId);
        }
      }, 3000);
      return (
        <div className="container min-height-100 d-flex justify-content-center align-items-center rounded">
          <div className="swish-error-container rounded bg-light">
            <div className="border p-3 rounded">
              <div className="row">
                <div className="col-12">
                  <h3>
                    {translatePage(
                      translations,
                      language,
                      "swishCheckoutPage",
                      "paymentSuccessH1"
                    )}
                  </h3>
                  <p>
                    {" "}
                    {translatePage(
                      translations,
                      language,
                      "swishCheckoutPage",
                      "paymentSuccessMessage"
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="container min-height-100 d-flex justify-content-center align-items-center rounded">
        <div className="swish-error-container rounded">
          <div className="border p-3 rounded">
            <SwishErrorComponent
              swishResponse={swishResponse}
              language={language}
              translations={translations}
            ></SwishErrorComponent>
            <div className="row">
              <div className="col-12">
                <button
                  onClick={() => navigate("/")}
                  className="btn btn-secondary w-100"
                >
                  {translatePage(
                    translations,
                    language,
                    "swishCheckoutPage",
                    "startPageButton"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <LoadingComponent />;
}

export default SwishCheckoutPage;
